import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Music2, Upload, ArrowLeft, CheckCircle2, XCircle, Clock, Edit3, Trash2, RefreshCw, ArrowRight, Users,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import KempenvisionNav from '@/components/KempenvisionNav'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select'
import { visionAPI } from '../lib/api'
import { useVisionSocket } from '../lib/visionSocket'
import TeamMark, { parseFlag } from '@/components/TeamMark'

const DEFAULT_FLAG = ['#ff2b8f', '#0b0518', '#00d9ff']



const STATUS = {
  pending:   { label: 'In behandeling', color: 'text-yellow-500 border-yellow-500/40', icon: Clock },
  approved:  { label: 'Goedgekeurd',     color: 'text-emerald-400 border-emerald-500/40', icon: CheckCircle2 },
  rejected:  { label: 'Afgekeurd',       color: 'text-red-500 border-red-500/40', icon: XCircle },
  withdrawn: { label: 'Opgegeven',       color: 'text-slate-400 border-slate-400/40', icon: XCircle },
}

/* ================================================================
   Main page — 2-stap flow:
   1. Inschrijven (team keuze)
   2. Nummer instellen (titel/genre/BPM/MP3)
   3. Status weergave (compleet)
================================================================ */
export default function KempenvisionInleverPage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editingSong, setEditingSong] = useState(false)
  const [editingTeam, setEditingTeam] = useState(false)
  const [error, setError] = useState(null)

  const load = async () => {
    setLoading(true)
    try { const { data } = await visionAPI.getMySubmission(); setData(data) }
    catch (e) { setError(e.message) } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])
  useVisionSocket((p) => { if (p.scope === 'show' || p.scope === 'submission') load() })

  if (loading) return <div className="p-6 text-muted-foreground">Laden…</div>
  if (!data) return <div className="p-6 text-red-500">Kon niet laden.</div>
  if (!data.show) return (
    <div className="max-w-2xl mx-auto">
      <KempenvisionNav />
      <Card><CardContent className="p-6 text-center text-muted-foreground">Geen actieve Kempenvision show.</CardContent></Card>
    </div>
  )

  const { show, teams, submission, genres } = data
  const open = show.submissions_open
  const hasSubmission = !!submission

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-24">
      <KempenvisionNav />
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-[4px] border border-border bg-card flex items-center justify-center shrink-0" style={{ borderLeft: "2px solid rgb(var(--acid-rgb))" }}>
          <Music2 className="h-6 w-6 text-acid" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-widest text-pink-400 font-semibold">Inschrijving</div>
          <h1 className="text-xl font-bold truncate">{show.name}{show.edition ? ` — ${show.edition}` : ''}</h1>
        </div>
        <Button asChild variant="ghost" size="sm"><Link to="/kempenvision"><ArrowLeft className="h-4 w-4" /></Link></Button>
      </div>

      {error && <Card className="border-red-500/40 bg-red-500/5"><CardContent className="p-3 text-sm text-red-500">⚠ {error}</CardContent></Card>}

      {/* Progress steps */}
      {(hasSubmission || open) && <ProgressSteps hasSub={hasSubmission} hasSong={hasSubmission && submission.title && submission.genre} hasAudio={hasSubmission && submission.audio_url} />}

      {/* Editing team (bestaande inzending) */}
      {editingTeam && hasSubmission && (
        <TeamForm
          teams={teams} initial={submission}
          onSaved={() => { setEditingTeam(false); load() }}
          onCancel={() => setEditingTeam(false)}
        />
      )}

      {/* Stap 1: Geen submission → inschrijving */}
      {!hasSubmission && open && !editingTeam && (
        <InschrijfCard teams={teams} onSaved={load} />
      )}

      {/* Geen submission + closed */}
      {!hasSubmission && !open && (
        <Card className="border-yellow-500/40 bg-yellow-500/5">
          <CardContent className="p-6 text-center">
            <div className="font-bold text-yellow-500 mb-2">Inschrijvingen zijn gesloten</div>
            <div className="text-sm text-muted-foreground">Volg de show via de <Link to="/kempenvision" className="underline">overzichtpagina</Link>.</div>
          </CardContent>
        </Card>
      )}

      {/* Stap 2: song info form — alleen als user er expliciet in klikt */}
      {hasSubmission && !editingTeam && editingSong && (
        <SongForm
          submission={submission} genres={genres} open={open}
          onSaved={() => { setEditingSong(false); load() }}
          onCancel={() => setEditingSong(false)}
        />
      )}

      {/* Status card altijd zichtbaar als er submission is en we niets aan het editten zijn */}
      {hasSubmission && !editingSong && !editingTeam && (
        <SubmissionStatus
          submission={submission} teams={teams}
          openForEdit={open}
          onEditSong={() => setEditingSong(true)}
          onEditTeam={() => setEditingTeam(true)}
          onReload={load}
        />
      )}
    </div>
  )
}

/* ============ Progress steps indicator ============ */
function ProgressSteps({ hasSub, hasSong, hasAudio }) {
  const steps = [
    { label: 'Ingeschreven', done: hasSub },
    { label: 'Nummer info',  done: hasSong },
    { label: 'MP3 upload',   done: hasAudio },
  ]
  return (
    <div className="flex items-center gap-1 text-xs">
      {steps.map((s, i) => (
        <div key={i} className="flex items-center gap-1 flex-1">
          <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
            s.done ? 'bg-emerald-500 text-white' : 'bg-muted text-muted-foreground'
          }`}>{s.done ? '✓' : i+1}</div>
          <span className={`truncate ${s.done ? 'text-emerald-400' : 'text-muted-foreground'}`}>{s.label}</span>
          {i < steps.length - 1 && <div className={`h-0.5 flex-1 mx-1 ${steps[i+1].done ? 'bg-emerald-500' : 'bg-muted'}`} />}
        </div>
      ))}
    </div>
  )
}

/* ============================================================
   Stap 1: INSCHRIJVEN — alleen team keuze
============================================================ */
function InschrijfCard({ teams, onSaved }) {
  const [teamChoice, setTeamChoice] = useState(teams.length > 0 ? 'existing' : 'new')
  const [teamId, setTeamId]         = useState(teams[0] ? String(teams[0].id) : '')
  const [newName, setNewName]       = useState('')
  // colors verwijderd: teams krijgen icoon van admin
  const [saving, setSaving]         = useState(false)
  const [error, setError]           = useState(null)

  const submit = async (e) => {
    e.preventDefault()
    setError(null)
    if (teamChoice === 'existing' && !teamId) { setError('Kies een team'); return }
    if (teamChoice === 'new' && !newName.trim()) { setError('Team naam vereist'); return }

    setSaving(true)
    try {
      const fd = new FormData()
      fd.append('team_choice', teamChoice)
      if (teamChoice === 'existing') fd.append('team_id', teamId)
      else {
        fd.append('proposed_team_name', newName.trim())
      }
      await visionAPI.createMySubmission(fd)
      onSaved()
    } catch (e) {
      setError(e.response?.data?.error ?? e.message)
    } finally { setSaving(false) }
  }

  return (
    <form onSubmit={submit}>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-pink-400" />
            Stap 1 · Schrijf je in
          </CardTitle>
          <p className="text-xs text-muted-foreground">Kies of maak een team. Je nummer + MP3 kan je in de volgende stap toevoegen — ook later nog.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="radio" checked={teamChoice === 'existing'} onChange={() => setTeamChoice('existing')} />
              Bestaand team
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="radio" checked={teamChoice === 'new'} onChange={() => setTeamChoice('new')} />
              Nieuw team maken
            </label>
          </div>

          {teamChoice === 'existing' && (
            teams.length === 0
              ? <div className="text-sm text-muted-foreground">Nog geen bestaande teams. Kies "Nieuw team maken".</div>
              : (
                <Select value={teamId} onValueChange={setTeamId}>
                  <SelectTrigger><SelectValue placeholder="Kies team" /></SelectTrigger>
                  <SelectContent>
                    {teams.map(t => <SelectItem key={t.id} value={String(t.id)}>{t.name}{t.code ? ` (${t.code})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              )
          )}

          {teamChoice === 'new' && (
            <div className="space-y-3">
              <div>
                <Label>Team naam *</Label>
                <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Team Basement" required />
              </div>
            </div>
          )}

          {error && <div className="text-sm text-red-500">⚠ {error}</div>}

          <Button type="submit" size="lg" className="w-full" disabled={saving}>
            {saving ? 'Bezig…' : <>Schrijf in <ArrowRight className="h-4 w-4 ml-2" /></>}
          </Button>
        </CardContent>
      </Card>
    </form>
  )
}

/* ============================================================
   Stap 2: SONG INFO — titel/artist/genre/BPM + MP3
============================================================ */
function SongForm({ submission, genres, open, onSaved, onCancel }) {
  const [title, setTitle]   = useState(submission.title ?? '')
  const [artist, setArtist] = useState(submission.artist_label ?? '')
  const [genre, setGenre]   = useState(submission.genre ?? '')
  const [bpm, setBpm]       = useState(submission.bpm ?? '')
  const [file, setFile]     = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState(null)

  const setGenreSug = (v) => {
    setGenre(v)
    if (!bpm) {
      const g = genres.find(x => x.key === v)
      if (g?.suggestedBpm) setBpm(g.suggestedBpm)
    }
  }

  const submit = async (e) => {
    e.preventDefault()
    setError(null)
    if (!title.trim()) { setError('Titel is verplicht'); return }
    if (!genre) { setError('Kies een genre'); return }
    setSaving(true)
    try {
      await visionAPI.updateMySubmission(submission.id, {
        title, artist_label: artist || null, genre,
        bpm: bpm ? Number(bpm) : null,
      })
      if (file) await visionAPI.replaceMyAudio(submission.id, file)
      onSaved()
    } catch (e) { setError(e.response?.data?.error ?? e.message) } finally { setSaving(false) }
  }

  return (
    <form onSubmit={submit}>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Music2 className="h-4 w-4 text-pink-400" />
            Stap 2 · Je nummer
          </CardTitle>
          <p className="text-xs text-muted-foreground">Titel + genre zijn nodig. MP3 mag ook later.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Nummer titel *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Neon Cathedral" required />
          </div>
          <div>
            <Label>Artiest label (optioneel)</Label>
            <Input value={artist} onChange={e => setArtist(e.target.value)} placeholder="Ferrite Choir" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Genre *</Label>
              <Select value={genre} onValueChange={setGenreSug}>
                <SelectTrigger><SelectValue placeholder="Kies genre" /></SelectTrigger>
                <SelectContent>
                  {genres.map(g => <SelectItem key={g.key} value={g.key}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>BPM (optioneel)</Label>
              <Input type="number" value={bpm} onChange={e => setBpm(e.target.value)} placeholder="128" />
            </div>
          </div>

          <div>
            <Label>MP3 upload {submission.audio_url ? '(vervangt huidige)' : '(optioneel — kan later)'}</Label>
            <label className="block cursor-pointer mt-1">
              <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-pink-500/40 transition-colors">
                <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                <div className="text-sm font-semibold">{file ? file.name : (submission.audio_url ? 'Kies nieuwe MP3' : 'Kies MP3 (max 30MB)')}</div>
                {file && <div className="text-xs text-muted-foreground mt-1">{(file.size / 1024 / 1024).toFixed(1)} MB</div>}
              </div>
              <input type="file" accept="audio/*,.mp3,.wav,.m4a,.ogg" className="hidden"
                     onChange={e => setFile(e.target.files?.[0] ?? null)} />
            </label>
            {submission.audio_url && !file && (
              <audio controls src={submission.audio_url} className="w-full h-8 mt-2" preload="none" />
            )}
          </div>

          {error && <div className="text-sm text-red-500">⚠ {error}</div>}

          <div className="flex gap-2 justify-end pt-2 border-t border-border">
            {onCancel && <Button type="button" variant="ghost" onClick={onCancel}>Annuleer</Button>}
            <Button type="submit" size="lg" disabled={saving}>
              {saving ? 'Opslaan…' : 'Opslaan'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  )
}

/* ============================================================
   Team wijzigen — alleen team, geen song info
============================================================ */
function TeamForm({ teams, initial, onSaved, onCancel }) {
  const [teamChoice, setTeamChoice] = useState('existing')
  const [teamId, setTeamId]         = useState(initial.team_id ? String(initial.team_id) : (teams[0] ? String(teams[0].id) : ''))
  const [newName, setNewName]       = useState(initial.team_name ?? '')
  // colors verwijderd: teams krijgen icoon van admin
  const [saving, setSaving]         = useState(false)
  const [error, setError]           = useState(null)

  const submit = async (e) => {
    e.preventDefault()
    setError(null)
    if (teamChoice === 'existing' && !teamId) { setError('Kies een team'); return }
    if (teamChoice === 'new' && !newName.trim()) { setError('Team naam vereist'); return }
    setSaving(true)
    try {
      await visionAPI.updateMySubmission(initial.id, {
        team_choice: teamChoice,
        team_id: teamChoice === 'existing' ? Number(teamId) : null,
        proposed_team_name: teamChoice === 'new' ? newName.trim() : null,
      })
      onSaved()
    } catch (e) { setError(e.response?.data?.error ?? e.message) } finally { setSaving(false) }
  }

  return (
    <form onSubmit={submit}>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Team wijzigen</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="radio" checked={teamChoice === 'existing'} onChange={() => setTeamChoice('existing')} /> Bestaand team
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="radio" checked={teamChoice === 'new'} onChange={() => setTeamChoice('new')} /> Nieuw team
            </label>
          </div>
          {teamChoice === 'existing' ? (
            <Select value={teamId} onValueChange={setTeamId}>
              <SelectTrigger><SelectValue placeholder="Kies team" /></SelectTrigger>
              <SelectContent>{teams.map(t => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <>
              <div><Label>Team naam</Label><Input value={newName} onChange={e => setNewName(e.target.value)} required /></div>
            </>
          )}
          {error && <div className="text-sm text-red-500">⚠ {error}</div>}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="ghost" onClick={onCancel}>Annuleer</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Opslaan…' : 'Opslaan'}</Button>
          </div>
        </CardContent>
      </Card>
    </form>
  )
}

/* ============================================================
   Status card
============================================================ */
function SubmissionStatus({ submission, teams, openForEdit, onEditSong, onEditTeam, onReload }) {
  const st = STATUS[submission.status] ?? STATUS.pending
  const Icon = st.icon
  const [replacing, setReplacing] = useState(false)
  const [error, setError] = useState(null)

  const canEdit = submission.status === 'pending' && openForEdit
  const canWithdraw = submission.status !== 'withdrawn' && submission.status !== 'rejected'
  const needsAudio = !submission.audio_url && submission.status === 'pending'
  const hasSongInfo = submission.title && submission.genre

  const del = async () => {
    if (!confirm('Inzending verwijderen? Kan niet ongedaan gemaakt worden.')) return
    try { await visionAPI.deleteMySubmission(submission.id); onReload() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
  }
  const withdraw = async () => {
    if (!confirm('Je weet zeker dat je opgeeft? Je nummer wordt uit de show gehaald.')) return
    try { await visionAPI.withdrawMySubmission(submission.id); onReload() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
  }
  const replaceAudio = async (e) => {
    const file = e.target.files?.[0]; if (!file) return
    setReplacing(true); setError(null)
    try { await visionAPI.replaceMyAudio(submission.id, file); onReload() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
    finally { setReplacing(false) }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-xs uppercase text-muted-foreground">Jouw inzending</div>
              <CardTitle className="text-lg truncate">{submission.title || <span className="text-muted-foreground italic">nog geen titel</span>}</CardTitle>
              <div className="text-sm text-muted-foreground mt-1">
                {submission.artist_label ? `${submission.artist_label} · ` : ''}
                <span className="uppercase">{submission.genre || 'geen genre'}</span>
                {submission.bpm ? ` · ${submission.bpm} BPM` : ''}
              </div>
            </div>
            <Badge variant="outline" className={st.color}>
              <Icon className="h-3 w-3 mr-1" />{st.label}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 p-3 rounded border border-border bg-card">
            <TeamMark team_icon_name={submission.team_icon_name} team_icon_color={submission.team_icon_color} team_logo_url={submission.team_logo_url} team_name={submission.team_name || submission.proposed_team_name} size="lg" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground uppercase">Team</div>
              <div className="font-semibold">{submission.team_name ?? '—'}</div>
            </div>
            {canEdit && (
              <Button variant="ghost" size="sm" onClick={onEditTeam}><Edit3 className="h-4 w-4" /></Button>
            )}
          </div>

          {submission.audio_url && (
            <div>
              <div className="text-xs text-muted-foreground uppercase mb-1">Audio</div>
              <audio controls src={submission.audio_url} className="w-full h-10" preload="none" />
              {submission.original_filename && <div className="text-xs text-muted-foreground mt-1 truncate">{submission.original_filename}</div>}
            </div>
          )}

          {!hasSongInfo && submission.status === 'pending' && (
            <div className="p-3 rounded border border-pink-500/40 bg-pink-500/5 text-sm">
              <div className="font-semibold text-pink-400 mb-2">🎵 Nummer info nog niet ingevuld</div>
              <div className="text-xs text-muted-foreground mb-2">Voeg titel + genre toe wanneer je wil — kan altijd.</div>
              <Button size="sm" onClick={onEditSong}>
                <Edit3 className="h-4 w-4 mr-2" />Vul nummer info in
              </Button>
            </div>
          )}

          {needsAudio && hasSongInfo && (
            <div className="p-3 rounded border border-yellow-500/40 bg-yellow-500/5 text-sm">
              <div className="font-semibold text-yellow-500 mb-2">📀 MP3 nog niet geüpload</div>
              <div className="text-xs text-muted-foreground mb-2">Je nummer info is klaar. Upload de MP3 wanneer je wil.</div>
              <label className="cursor-pointer">
                <Button variant="outline" size="sm" asChild disabled={replacing}>
                  <span><Upload className={`h-4 w-4 mr-2 ${replacing ? 'animate-spin' : ''}`} />{replacing ? 'Uploaden…' : 'Upload MP3'}</span>
                </Button>
                <input type="file" accept="audio/*,.mp3,.wav,.m4a,.ogg" className="hidden" onChange={replaceAudio} />
              </label>
            </div>
          )}

          {submission.status === 'rejected' && submission.admin_notes && (
            <div className="p-3 rounded border border-red-500/40 bg-red-500/5 text-sm">
              <div className="font-semibold text-red-400 mb-1">Reden afkeuring:</div>{submission.admin_notes}
            </div>
          )}

          {error && <div className="text-sm text-red-500">⚠ {error}</div>}
        </CardContent>
      </Card>

      {canEdit && (
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={onEditSong}><Edit3 className="h-4 w-4 mr-2" />Wijzig nummer info</Button>
          {submission.audio_url && (
            <label className="cursor-pointer">
              <Button variant="outline" asChild disabled={replacing}>
                <span><RefreshCw className={`h-4 w-4 mr-2 ${replacing ? 'animate-spin' : ''}`} />{replacing ? 'Uploaden…' : 'Vervang MP3'}</span>
              </Button>
              <input type="file" accept="audio/*,.mp3,.wav,.m4a,.ogg" className="hidden" onChange={replaceAudio} />
            </label>
          )}
          <Button variant="ghost" onClick={del}><Trash2 className="h-4 w-4 mr-2 text-red-500" />Verwijder</Button>
        </div>
      )}

      {canWithdraw && (
        <div className="flex justify-center pt-2 border-t border-border/30 mt-2">
          <Button variant="ghost" size="sm" onClick={withdraw} className="text-slate-400">
            <XCircle className="h-4 w-4 mr-2" />Ik geef op
          </Button>
        </div>
      )}

      {submission.status === 'withdrawn' && (
        <Card className="border-slate-500/40 bg-slate-500/5">
          <CardContent className="p-4 text-center text-sm text-muted-foreground">
            Je hebt deze inzending opgegeven. Vraag een admin om opnieuw te mogen inschrijven.
          </CardContent>
        </Card>
      )}
    </div>
  )
}
